import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CreditCard, Flag, Loader2, PackageCheck, Search, Truck } from "lucide-react";
import api from "@/lib/api";
import { LogoBig } from "@/components/Logo";

const STEPS = [
  { key: "en_attente_paiement", label: "En attente de paiement", icon: CreditCard, text: "Le paiement n'est pas encore finalisé." },
  { key: "confirmee", label: "Confirmée", icon: PackageCheck, text: "Votre livraison est confirmée. Angélique vous appellera pour caler le moment exact." },
  { key: "en_route", label: "En route", icon: Truck, text: "Angélique est en chemin avec votre commande." },
  { key: "livree", label: "Livrée", icon: Flag, text: "Votre commande a été remise en main propre. Merci de votre confiance !" },
];

const ORDER = ["en_attente_paiement", "confirmee", "en_route", "livree"];

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function Tracking() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (value) => {
    const clean = value.trim();
    if (!clean) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(clean)}`);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Aucune livraison ne correspond à cette référence.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = params.get("ref");
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentIndex = result ? ORDER.indexOf(result.status) : -1;
  const cancelled = result?.status === "annulee";

  return (
    <div className="min-h-screen bg-cream px-4 py-12">
      <div className="mx-auto max-w-xl">
        <div className="mb-10 flex justify-center">
          <Link to="/" aria-label="Retour à l'accueil">
            <LogoBig />
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-ink/5 sm:p-10"
          data-testid="tracking-page"
        >
          <h1 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">Suivre ma livraison</h1>
          <p className="mt-2 text-sm text-ink/60 sm:text-base">
            Entrez la référence reçue après votre paiement (elle commence par PC-).
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              search(ref);
            }}
            className="mt-6 flex gap-3"
          >
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="PC-XXXXXX"
              data-testid="tracking-ref-input"
              className="min-h-[52px] flex-1 rounded-xl border border-ink/15 bg-white px-4 font-mono text-base uppercase tracking-wider text-ink outline-none placeholder:normal-case placeholder:tracking-normal placeholder:text-ink/35 focus:border-forest focus:ring-2 focus:ring-forest/20"
            />
            <button
              type="submit"
              disabled={loading || !ref.trim()}
              data-testid="tracking-search-button"
              className="flex min-h-[52px] items-center gap-2 rounded-full bg-forest px-6 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Suivre
            </button>
          </form>

          {error && (
            <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="tracking-error">
              {error} Vérifiez la référence ou appelez le 06 73 31 82 36.
            </p>
          )}

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
              data-testid="tracking-result"
            >
              <div className="rounded-2xl bg-cream p-5 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink/55">Référence</span>
                  <span className="font-mono font-semibold text-ink">{result.ref}</span>
                </div>
                <div className="mt-2 flex justify-between">
                  <span className="text-ink/55">Prestation</span>
                  <span className="font-semibold text-ink">{result.delivery_label}</span>
                </div>
                <div className="mt-2 flex justify-between">
                  <span className="text-ink/55">Date</span>
                  <span className="font-semibold text-ink">
                    {result.date} — {result.time_slot}
                  </span>
                </div>
                <div className="mt-2 flex justify-between">
                  <span className="text-ink/55">Livraison à</span>
                  <span className="max-w-[55%] text-right font-semibold text-ink">{result.delivery_address}</span>
                </div>
                <div className="mt-2 flex justify-between">
                  <span className="text-ink/55">Paiement</span>
                  <span className="font-semibold text-ink" data-testid="tracking-payment-label">{result.payment_status_label}</span>
                </div>
                <div className="mt-2 flex justify-between border-t border-ink/10 pt-2">
                  <span className="text-ink/55">Montant</span>
                  <span className="font-serif text-base font-semibold text-forest">{eur(result.total)}</span>
                </div>
              </div>

              {cancelled ? (
                <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="tracking-cancelled">
                  Cette livraison a été annulée. Appelez le 06 73 31 82 36 pour plus d'informations.
                </p>
              ) : (
                <ol className="mt-8 space-y-0" data-testid="tracking-steps">
                  {STEPS.map((step, i) => {
                    const done = currentIndex > i;
                    const active = currentIndex === i;
                    return (
                      <li key={step.key} className="relative flex gap-4 pb-8 last:pb-0">
                        {i < STEPS.length - 1 && (
                          <span
                            className={`absolute left-[23px] top-12 h-[calc(100%-3rem)] w-0.5 ${done ? "bg-forest" : "bg-ink/10"}`}
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors ${
                            done
                              ? "bg-forest text-cream"
                              : active
                                ? "bg-terra text-white shadow-lg shadow-terra/30"
                                : "bg-ink/5 text-ink/35"
                          }`}
                        >
                          {done ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                        </span>
                        <div className="pt-1.5">
                          <p className={`font-semibold ${active ? "text-terra-dark" : done ? "text-forest" : "text-ink/40"}`}>
                            {step.label}
                          </p>
                          {active && <p className="mt-1 text-sm leading-relaxed text-ink/60">{step.text}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </motion.div>
          )}
        </motion.div>

        <p className="mt-8 text-center text-sm text-ink/50">
          Une question ? <a href="tel:+33673318236" className="font-semibold text-forest hover:underline">06 73 31 82 36</a>
          {" · "}
          <Link to="/" className="font-semibold text-forest hover:underline" data-testid="tracking-back-home">
            Retour à l'accueil
          </Link>
        </p>
      </div>
    </div>
  );
}
